import {DateTime} from 'luxon';
import {
  getInspectionInspectedByName,
  getUserDisplayName,
  numberWithCommas,
  unitsLengthDisplayConversion,
} from '../../app/utils';
import BlockIcon from '@mui/icons-material/Block';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ConstructionIcon from '@mui/icons-material/Construction';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import PublishedWithChangesIcon from '@mui/icons-material/PublishedWithChanges';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import RemoveIcon from '@mui/icons-material/Remove';
import SearchIcon from '@mui/icons-material/Search';
import SyncIcon from '@mui/icons-material/Sync';

// ============================================================================
// Submission Timeout
// ============================================================================
// Shared timeout for Shopview modal submissions (fetchPostAuthSafeWithTimeout).
// Long enough to tolerate a slow-but-real submission; still bounded so a dead
// connection fails and clears the submitting state instead of hanging forever.
// The upserts can be slow at times so hopefully 45 seconds doesn't cause any "false"
// timeouts where the change goes through but the user gets bounced to the error page.
export const SHOPVIEW_SUBMISSION_TIMEOUT_MS = 60 * 1000;

// ============================================================================
// Service Entry Status Options
// ============================================================================
export const SERVICE_STATUS_OPTIONS = {
  not_started: {
    value: 'not_started',
    label: 'Not Started',
    color: '#9da5b1',
    bgColor: '#f4f5f7',
    icon: <MoreHorizIcon fontSize='small' />,
    order: 1,
  },
  in_progress: {
    value: 'in_progress',
    label: 'In Progress',
    color: '#007bff',
    bgColor: '#e7f1ff',
    icon: <SyncIcon fontSize='small' />,
    order: 2,
  },
  blocked: {
    value: 'blocked',
    label: 'Blocked',
    color: '#c44b16',
    bgColor: '#fce4d6',
    icon: <BlockIcon fontSize='small' />,
    order: 3,
  },
  dismissed: {
    value: 'dismissed',
    label: 'Dismissed',
    color: '#d9534f',
    bgColor: '#fce8e8',
    icon: <CancelOutlinedIcon fontSize='small' />,
    order: 4,
  },
  done: {
    value: 'done',
    label: 'Done',
    color: '#28a745',
    bgColor: '#e8f5e9',
    icon: <CheckCircleIcon fontSize='small' />,
    order: 5,
  },
};

// Array version for dropdowns and iteration
export const SERVICE_STATUS_LIST = Object.values(SERVICE_STATUS_OPTIONS);

// ============================================================================
// Service Entry Priority Options
// ============================================================================
export const SERVICE_PRIORITY_OPTIONS = {
  high: {
    value: 'high',
    label: 'High',
    color: '#dc3545',
    bgColor: '#fce8e8',
    icon: <KeyboardArrowUpIcon fontSize='small' />,
    order: 1,
  },
  medium: {
    value: 'medium',
    label: 'Medium',
    color: '#8d6e3f',
    bgColor: '#f5ebe0',
    icon: <RemoveIcon fontSize='small' />,
    order: 2,
  },
  low: {
    value: 'low',
    label: 'Low',
    color: '#7b8794',
    bgColor: '#eef0f3',
    icon: <KeyboardArrowDownIcon fontSize='small' />,
    order: 3,
  },
};

// Array version for dropdowns and iteration
export const SERVICE_PRIORITY_LIST = Object.values(SERVICE_PRIORITY_OPTIONS);

// ============================================================================
// Service Task Display Name Helper
// ============================================================================

/**
 * Returns the appropriate display name for a service task based on user language.
 * For inspection failure tasks, builds the name with the correct prefix and
 * falls back to inspectionItemsLookUp if nameEs is not stored on the task.
 *
 * @param {Object} serviceTask - The service task object
 * @param {string} userLanguage - The user's language setting (e.g. 'es', 'en')
 * @param {Object} inspectionItemsLookUp - Lookup dict of inspection items by ID
 * @return {string} The display name for the service task
 */
export function getServiceTaskDisplayName(serviceTask, userLanguage, inspectionItemsLookUp = {}) {
  if (userLanguage === 'es') {
    // If task has a Spanish name, use it
    if (serviceTask.name_es) {
      return serviceTask.name_es;
    }
    // For inspection failure tasks without name_es, build from inspection item
    if (
      serviceTask.inspection_failure &&
      serviceTask.inspection_item_id &&
      inspectionItemsLookUp[serviceTask.inspection_item_id]
    ) {
      const item = inspectionItemsLookUp[serviceTask.inspection_item_id];
      const itemNameEs = item.name_es || item.name;
      return `Inspección Fallida: ${itemNameEs}`;
    }
  }
  return serviceTask.name;
}

// ============================================================================
// Maintenance Due Lines
// ============================================================================

function isEmptyMetric(value) {
  return value === null || value === undefined || value === '';
}

/**
 * How far a maintenance item is from (or past) due, one line per interval it tracks.
 * Shared by ShopView's Upcoming tab and CabView's overdue services modal so both show
 * the same amounts.
 *
 * @param {Object} maintenanceItem - Item from the worker's generateMaintenanceReport
 * @param {Function} t - i18next translate function
 * @param {string} unitsLength - 'km' or 'mi'
 * @return {Array<{text: string, overdue: boolean}>} overdue lines are the ones ShopView bolds
 */
export function getMaintenanceDueLines(maintenanceItem, t, unitsLength) {
  const serviceTask = maintenanceItem.serviceTask;
  const isImplementItem = maintenanceItem.implement_sn && !maintenanceItem.vehicle_sn;
  const serviceDetails = isImplementItem
    ? serviceTask?.service_details_by_implement_sn?.[maintenanceItem.implement_sn] || {}
    : serviceTask?.service_details_by_vehicle_sn?.[maintenanceItem.vehicle_sn] || {};
  const lines = [];

  if (
    serviceTask.time_interval ||
    (serviceDetails.next_service_date &&
      (maintenanceItem.noHistory || (serviceTask.inspection_report_ids && serviceTask.inspection_report_ids.length > 0)))
  ) {
    const timeDiff = maintenanceItem.timeDiffDays;

    // Determine display based on units
    let unitsDisplay = t('Days');
    let timeDisplay = Math.abs(timeDiff);
    if (timeDisplay >= 30) {
      unitsDisplay = t('Months');
      timeDisplay = Math.floor(timeDisplay / 30);
    } else if (timeDisplay >= 7) {
      unitsDisplay = t('Weeks');
      timeDisplay = Math.floor(timeDisplay / 7);
    }

    if (serviceTask.inspection_report_ids && serviceTask.inspection_report_ids.length > 0) {
      lines.push({text: `${timeDisplay} ${unitsDisplay} ${t('Since Reported')}`, overdue: true});
    } else if (serviceTask.breakdown_report_ids && serviceTask.breakdown_report_ids.length > 0) {
      lines.push({text: `${timeDisplay} ${unitsDisplay} ${t('Since Reported')}`, overdue: true});
    } else if (timeDiff < 0) {
      lines.push({text: `${timeDisplay} ${unitsDisplay} ${t('Overdue')}`, overdue: true});
    } else {
      lines.push({text: `${timeDisplay} ${unitsDisplay}`, overdue: false});
    }
  }
  if (
    !isImplementItem &&
    (serviceTask.odometer_interval || (serviceDetails.next_service_odometer && maintenanceItem.noHistory))
  ) {
    const odometerDiff = maintenanceItem.odometerDiffKm;
    if (!isEmptyMetric(odometerDiff)) {
      const distance = numberWithCommas(Math.abs(unitsLengthDisplayConversion(odometerDiff, unitsLength)).toFixed(0));
      lines.push(
        odometerDiff < 0
          ? {text: `${distance} ${unitsLength} ${t('Overdue')}`, overdue: true}
          : {text: `${distance} ${unitsLength}`, overdue: false}
      );
    }
  }
  if (
    !isImplementItem &&
    (serviceTask.engine_hours_interval || (serviceDetails.next_service_engine_hours && maintenanceItem.noHistory))
  ) {
    const engineHoursDiff = maintenanceItem.engineHoursDiff;
    if (!isEmptyMetric(engineHoursDiff)) {
      const hours = numberWithCommas(Math.round(Math.abs(engineHoursDiff)));
      lines.push(
        engineHoursDiff < 0
          ? {text: `${hours} ${t('Engine Hours')} ${t('Overdue')}`, overdue: true}
          : {text: `${hours} ${t('Engine Hours')}`, overdue: false}
      );
    }
  }
  // Handle implement hours for implement services
  if (serviceTask.implement_hours_interval || (serviceDetails.next_implement_usage_hours && maintenanceItem.noHistory)) {
    const implementUsageHoursDiff = maintenanceItem.implementUsageHoursDiff;
    if (implementUsageHoursDiff !== null && implementUsageHoursDiff !== undefined) {
      const hours = numberWithCommas(Math.round(Math.abs(implementUsageHoursDiff)));
      lines.push(
        implementUsageHoursDiff < 0
          ? {text: `${hours} ${t('Implement Hours')} ${t('Overdue')}`, overdue: true}
          : {text: `${hours} ${t('Implement Hours')}`, overdue: false}
      );
    }
  }
  return lines;
}

// ============================================================================
// Inspection Failure Banner
// ============================================================================

/**
 * Ids of a task's linked inspection reports not yet resolved by any service entry.
 *
 * @param {Object} serviceTask - Service task doc with inspection_report_ids
 * @param {Array} historyItems - History items whose serviceEntry may hold resolved inspection_report_ids
 * @return {Array} Open report ids, in submission order (last one is the newest)
 */
export function getOpenInspectionReportIds(serviceTask, historyItems) {
  if (!serviceTask?.inspection_report_ids?.length) {
    return [];
  }
  const closedReportIds = (historyItems || [])
    .filter((entry) => {
      return entry.serviceEntry?.service_task_id === serviceTask.id;
    })
    .flatMap((entry) => {
      return entry.serviceEntry?.inspection_report_ids || [];
    });
  return serviceTask.inspection_report_ids.filter((id) => {
    return !closedReportIds.includes(id);
  });
}

/**
 * Build the inspection-failure banner text from a linked inspection report doc,
 * so re-reported tasks describe the latest inspection instead of the stored creation-time notes.
 *
 * @param {Object} report - Inspection report doc (newest open linked report), from the shopview slice lookup
 * @param {string} timeZone - Customer display timezone
 * @param {Object} databaseUserNames - Map of user uid -> {name, username}
 * @param {Function} t - i18n translate function
 * @return {string} Formatted banner text, or '' when no report is available
 */
export function buildInspectionFailureBannerText(report, timeZone, databaseUserNames, t) {
  if (!report?.datetime) {
    return '';
  }
  const reportedDateString = DateTime.fromISO(report.datetime).setZone(timeZone).toFormat('yyyy-MM-dd hh:mm a');
  const inspectedBy = getInspectionInspectedByName(
    {
      inspectedByOperator: report.inspected_by_operator,
      inspectionNameInput: report.inspection_name_input,
      inspectedByUser: report.inspected_by_user,
    },
    {},
    databaseUserNames
  );
  let bannerText = `${t('Service created automatically to resolve failed inspection on')} ${reportedDateString}.`;
  if (inspectedBy) {
    bannerText += ` ${t('Inspected by')} '${inspectedBy}'.`;
  } else {
    const submittedBy = getUserDisplayName(databaseUserNames[report.inspected_by_user], report.inspected_by_user);
    if (submittedBy) {
      bannerText += ` ${t('Submitted by')} '${submittedBy}'.`;
    }
  }
  return bannerText;
}

// ============================================================================
// Checklist Status Options
// ============================================================================
export const checklistStatusOptions = {
  'i': {
    value: 'i',
    label: 'Inspected',
    icon: <SearchIcon />,
  },
  'r': {
    value: 'r',
    label: 'Repaired',
    icon: <ConstructionIcon />,
  },
  'c': {
    value: 'c',
    label: 'Changed',
    icon: <PublishedWithChangesIcon />,
  },
  'n': {
    value: 'n',
    label: 'N/A',
  },
};
